import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { ApiError, api } from '@/lib/api';
import type { DocuSignEnvelope } from '@/lib/types';

/*
 * SIGNATURE STATE, IN ONE REQUEST FOR THE WHOLE PAGE.
 *
 * `GET /docusign/envelopes` filters by a SINGLE `quoteId`, so the obvious
 * implementation — ask per row — costs one request per quote on screen. At a
 * caller's `perPage` of 20 that is 20 requests per page load, and at 100 it is
 * 100: **a cost proportional to the page, paid on every page load, for a
 * decoration.** It is not an option, and the shape below is not a micro-
 * optimisation of it — it is a different question asked once.
 *
 * ⚠️ *SUPERSEDED: "against a rate limit of **120 per 60 seconds per IP shared by
 * everyone behind the office NAT**. Six page-throughs by one person would spend
 * the entire office budget."* The limit is **12,000/60s** (corrected
 * 2026-09-17), so six page-throughs spend a rounding error of it. ⛔ **The
 * refusal is unchanged: a per-row fan-out fails a row at a time, and a missing
 * envelope renders as a quote nobody has sent for signature.** The constant
 * request count below is the reason, not the ceiling.
 *
 * WHAT MAKES ONE REQUEST SUFFICIENT, and it is a contract guarantee rather than
 * an assumption about the data: **there is at most ONE outstanding envelope per
 * quote.** A second send while one is still outstanding is a `409` naming the
 * count (see `DocuSignTemplateEnvelopeInput` in `@/lib/types`), so the set of
 * outstanding envelopes IS a set of distinct quotes. Asking for all of them once
 * and matching client-side therefore cannot double-count, and cannot miss a
 * second envelope hiding behind the one it found.
 *
 * SO THE REQUEST COUNT IS CONSTANT, not proportional to the page:
 *
 *     20 rows on screen  ->  1 request      (per-row would be 20)
 *    100 rows on screen  ->  1 request      (per-row would be 100)
 *
 * `outstanding: true` — everything not yet completed, declined or voided — and
 * NOT an unfiltered fetch, which would look equivalent and is not. Terminal
 * envelopes accumulate for ever, so an unfiltered list breaches the 100 ceiling
 * permanently and the coverage guarantee below would die and never come back.
 * Outstanding envelopes are a WORKING SET that drains: every one of them is
 * waiting on a person, and stops being outstanding when that person acts.
 *
 * ⚠️ **THE COST, AND IT MUST BE WORDED INTO THE UI: a blank cell means "nothing
 * is awaiting signature", NOT "nothing was ever sent".** A quote whose envelope
 * completed, was declined or was voided is deliberately not in this response and
 * shows no badge. That is the honest reading of the query we sent, and it is why
 * the column is called "Awaiting signature" rather than "Signature".
 *
 * ⚠️ **AND A `completed` ENVELOPE THEREFORE CANNOT REACH THIS LIST**, which is
 * what settles the bare-green-tick hazard structurally rather than by copy: the
 * one status carrying `claimsSignature` is excluded by the query. Note the
 * belt-and-braces if that ever changes — `EnvelopeStatusBadge`'s `connection`
 * prop is deliberately NOT passed by `SignatureCell`, and omitting it resolves
 * through `signaturesNotBinding()` to *warn*, never to reassure. The badge would
 * qualify a completed envelope as "Signed — not binding" on its own. The
 * connection is not fetched because `GET /docusign/connection` is ADMIN_ROLES:
 * reading it here would 403 on every quotes-list load for every non-admin,
 * filling the audit log to change nothing on a badge that cannot render that
 * status anyway.
 */

/** The ceiling on `GET /docusign/envelopes`. The default of 50 truncates in silence. */
export const OUTSTANDING_PER_PAGE = 100;

/**
 * What a caller knows about who is waiting on a signature.
 *
 * `unavailable` is its own state rather than an error: a 404 means the backend
 * has not mounted its DocuSign half, which this app renders calmly everywhere
 * else (`XeroPanel`, `EnvelopePanel`) rather than as a failure.
 *
 * ⚠️ `error` is NOT collapsed into "no envelope". A blank cell asserts that
 * nothing is awaiting signature, and after a failed read we do not know that —
 * so the cell says so instead of quietly claiming the safe-looking answer.
 */
export type SignatureState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      byQuoteId: ReadonlyMap<number, DocuSignEnvelope>;
      /**
       * How many outstanding envelopes exist that we did NOT fetch.
       *
       * Zero is the normal case and means the match is COMPLETE: every quote on
       * every page of the calling list has been checked against every outstanding
       * envelope in the system. Above zero we hold only the newest 100 and a row
       * on screen may be out for signature without showing it — which is
       * indistinguishable from that envelope not existing, so it is said out
       * loud rather than left to look like an answer.
       */
      unfetched: number;
    };

/**
 * Index the envelopes by the quote they settle.
 *
 * The contract says one outstanding envelope per quote, so a collision cannot
 * happen — and this keeps the FIRST rather than the last anyway, because the
 * server orders newest first and the newest is the one a person would act on. A
 * `Map` built the other way round would silently prefer the stalest row if that
 * guarantee ever slipped.
 */
export function indexByQuote(
  envelopes: readonly DocuSignEnvelope[],
): Map<number, DocuSignEnvelope> {
  const byQuoteId = new Map<number, DocuSignEnvelope>();
  for (const envelope of envelopes) {
    if (!byQuoteId.has(envelope.quote.id)) byQuoteId.set(envelope.quote.id, envelope);
  }
  return byQuoteId;
}

/**
 * Fold an envelope the server just handed back into what the calling page knows.
 *
 * ⚠️ **RENAMED from `recordSentEnvelope`, because it now has THREE callers and
 * only one of them is a send** — a reminder comes back with `lastRemindedAt`
 * moved, and the void inside "Change signer and resend" comes back `voided`. The
 * body never was send-specific; the name was, and a name that describes one
 * caller is how a second caller ends up with its own copy.
 *
 * The authoritative re-read happens anyway — see the caller's `reloadKey` — and
 * this exists only to close the window between the write returning and that
 * response arriving, during which the row would still read "not awaiting
 * signature" and its Send button would still be enabled. The envelope is the
 * record the server just handed back, so this is not a guess about the answer;
 * it is the answer, early.
 *
 * ⚠️ **A non-`ready` state is left ALONE, deliberately.** Promoting `loading`,
 * `error` or `unavailable` to `ready` on the strength of one envelope would say
 * the whole outstanding set is known and consists of this — so every OTHER row
 * on screen would render a blank cell, which on the calling page asserts "nothing
 * is awaiting signature". One known fact must not manufacture an answer about the
 * rows it says nothing about. `unfetched` is carried through untouched for the
 * same reason: a send tells us nothing about how much of the set we hold.
 */
export function recordEnvelope(
  state: SignatureState,
  envelope: DocuSignEnvelope,
): SignatureState {
  if (state.kind !== 'ready') return state;
  const byQuoteId = new Map(state.byQuoteId);
  byQuoteId.set(envelope.quote.id, envelope);
  return { ...state, byQuoteId };
}

/**
 * Drop an envelope the calling page just watched go terminal.
 *
 * The mirror of {@link recordEnvelope}, and it exists for one caller: the void
 * inside "Change signer and resend". **This map is the OUTSTANDING set** —
 * `outstanding: true` is the query behind it — and a voided envelope is not in
 * it any more, so leaving the row in place would keep the badge showing a
 * request that is dead and keep Send refused on `ONE_ENVELOPE_PER_QUOTE_REASON`,
 * which is the one refusal that has just stopped being true. The authoritative
 * re-read follows; this closes the window before it lands, exactly as the send
 * path does.
 *
 * ⚠️ **`unfetched` is carried through untouched**, and a non-`ready` state is
 * left alone, for the same reasons spelled out on {@link recordEnvelope}: one
 * known fact must not manufacture an answer about the rows it says nothing
 * about.
 */
export function forgetEnvelope(state: SignatureState, quoteId: number): SignatureState {
  if (state.kind !== 'ready') return state;
  if (!state.byQuoteId.has(quoteId)) return state;
  const byQuoteId = new Map(state.byQuoteId);
  byQuoteId.delete(quoteId);
  return { ...state, byQuoteId };
}

/**
 * Has this row anything to SAY about signatures?
 *
 * The table renders every state, including an em-dash for "not awaiting
 * signature", because a fixed column with a hole in it reads as a rendering
 * fault. A card has no column to keep, so it renders only the states that carry
 * information and stays silent otherwise — a stack of em-dashes down a phone
 * screen is noise, and the note above the list already says what a blank means.
 *
 * Written as a positive predicate rather than inline negations at the call site:
 * `!(ready && !has)` is correct and is exactly the kind of expression that gets
 * misread the next time somebody adds a state to {@link SignatureState}.
 */
export function hasSignatureToShow(state: SignatureState, quoteId: number): boolean {
  if (state.kind === 'unavailable') return false;
  if (state.kind === 'ready') return state.byQuoteId.has(quoteId);
  // `loading` and `error` both say something a reader needs: "still asking" and
  // "we do not know", neither of which may be shown as an absence.
  return true;
}

/**
 * THE ONE SIGNATURE REQUEST.
 *
 * Deliberately NOT keyed on `page`, `search` or the filters. The outstanding
 * envelopes are the same set whichever slice of the quote list is on screen,
 * so paging through five pages of quotes still costs exactly one of these —
 * which is the whole point of matching client-side rather than asking per row.
 *
 * Not debounced either, for the same reason: nothing about it changes as
 * somebody types.
 *
 * Skipped entirely while `enabled` is false — on the quotes list that is the
 * board, exactly as the list request is. The board shows no signature state
 * (see the note where it is mounted), so a request for it there would be spent
 * out of the shared office rate limit to render nothing. Re-entering the table
 * view refetches, which is deliberate: the stored status only moves when
 * something calls `/refresh` — a person, or the backend's own timer — so the
 * freshest copy a caller can offer is the one it just asked for.
 *
 * The setter is returned so a caller can still fold in its own optimistic
 * {@link recordEnvelope} / {@link forgetEnvelope} updates after a send or a
 * void.
 */
export function useOutstandingEnvelopes(
  enabled: boolean,
  reloadKey: number,
): [SignatureState, Dispatch<SetStateAction<SignatureState>>] {
  const [signatures, setSignatures] = useState<SignatureState>({ kind: 'loading' });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();

    /*
     * Deliberately NOT reset to `loading` here, and it is not only to satisfy
     * `react(set-state-in-effect)`. The initial state is already `loading`, so
     * the first render shows skeletons; the only thing a reset would change is
     * the board -> table return, where it would throw away badges that are still
     * the last thing the server said and flash skeletons in their place.
     *
     * Keeping them is the honest option as well as the calmer one: every one of
     * these is a LAST KNOWN status that only moves when something calls
     * `/refresh`, and the badge dates its own staleness from `statusCheckedAt`.
     * Briefly showing the previous answer is exactly what this column means.
     */
    api
      .listDocusignEnvelopes({ outstanding: true, perPage: OUTSTANDING_PER_PAGE }, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setSignatures({
          kind: 'ready',
          byQuoteId: indexByQuote(data.data),
          /*
           * `meta.total` is the whole outstanding set; `data.length` is what we
           * were given. `Math.max(…, 0)` because a total that lags the page it
           * came with must never render as a negative count of missing rows.
           */
          unfetched: Math.max(data.meta.total - data.data.length, 0),
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        /*
         * 401 is left to `api.ts`, which attempts one refresh and then ends the
         * session — a message here would contradict it midway through signing
         * out. 404 means the backend has not mounted its DocuSign half, which is
         * a state to render calmly rather than a failure. Same shape as
         * `EnvelopePanel`, deliberately.
         *
         * Everything else keeps the quote list intact and reports on the column
         * alone: a decoration failing to load must not take down the page it
         * decorates.
         */
        if (caught instanceof ApiError && caught.status === 401) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setSignatures({ kind: 'unavailable' });
          return;
        }
        setSignatures({
          kind: 'error',
          message: caught instanceof Error ? caught.message : 'The request failed.',
        });
      });

    return () => controller.abort();
    // `reloadKey` is a re-run token, never read — see where it is declared by
    // the caller. A send is the one thing that changes the outstanding set, so
    // it is the one thing that re-asks for it.
  }, [enabled, reloadKey]);

  return [signatures, setSignatures];
}
